import React, { useCallback, useEffect, useRef } from 'react'
import { useQuery, gql } from '@apollo/client'
import Map, { Marker, Source, Layer } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Alert, Box, Chip, CircularProgress, Divider, Typography } from '@mui/material'
import { truckDeliveryToday } from '../../apollo'
import ConfigurableValues from '../../config/constants'
import { circleToPolygonPath, MILES_TO_KM } from '../../utils/drivingArea'
import { ZONE_COLORS } from './ZoneEditor'

const GET_TRUCK_DELIVERY = gql`${truckDeliveryToday}`

const STATUS = {
  NOW: { label: 'Here now', sx: { bgcolor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' } },
  LATER: { label: 'Later today', sx: { bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' } },
  DONE: { label: 'Finished', sx: { bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' } },
}

function circleFeature(stop, miles) {
  const ring = circleToPolygonPath({ lat: stop.latitude, lng: stop.longitude }, miles * MILES_TO_KM)
    .map(p => [p.lng, p.lat])
  ring.push(ring[0])
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }
}

const hasPoint = s => s.latitude != null && s.longitude != null

/**
 * A food truck has no drawn boundary: it delivers a straight-line radius around
 * wherever each truck is parked. This shows today's stops for every truck with
 * that circle around each, so the super admin can see where delivery reaches.
 */
export default function TruckDeliveryToday({ restaurantId }) {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const mapRef = useRef(null)
  const { data, loading, error } = useQuery(GET_TRUCK_DELIVERY, {
    variables: { restaurantId },
    fetchPolicy: 'network-only',
    pollInterval: 60000,
  })

  const day = data?.truckDeliveryToday
  const trucks = day?.trucks || []
  const radius = day?.deliveryEnabled ? day.radiusMiles : null
  // Finished stops keep their pin but lose their circle: nothing delivers from there now.
  const pins = trucks.flatMap((t, ti) => (t.stops || [])
    .filter(s => hasPoint(s.stop))
    .map(s => ({ ...s, truckName: t.name, color: ZONE_COLORS[ti % ZONE_COLORS.length] })))
  const circles = radius ? pins.filter(p => p.status !== 'DONE') : []
  // Refit when the stops change, not on every poll that returns the same ones.
  const pinKey = pins.map(p => `${p.stop._id}:${p.status}`).join(',')

  const fitToContent = useCallback(() => {
    const map = mapRef.current
    if (!map || !pins.length) return
    const pts = circles.length
      ? circles.flatMap(p => circleFeature(p.stop, radius).geometry.coordinates[0])
      : pins.map(p => [p.stop.longitude, p.stop.latitude])
    const lngs = pts.map(p => p[0]); const lats = pts.map(p => p[1])
    map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 50, maxZoom: 13, duration: 400 })
  }, [pinKey, radius])

  useEffect(() => { fitToContent() }, [fitToContent])

  if (loading && !day) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error.message}</Alert>
  if (!day) return null

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 560, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Trucks and today's stops */}
      <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" fontWeight={700}>Today · {day.dateLabel}</Typography>
        {radius
          ? <Typography variant="body2" color="text.secondary" mt={0.5}>
              Delivers <b>{radius} mi</b> (straight line) around wherever each truck is parked.
            </Typography>
          : <Alert severity={day.deliveryEnabled ? 'warning' : 'info'} sx={{ mt: 1, borderRadius: 2 }}>
              {day.deliveryEnabled
                ? 'Delivery from the truck is on, but no radius is set, so nothing can be delivered.'
                : 'Pickup only. Delivery from the truck is off.'}
            </Alert>}
        <Typography variant="caption" color="text.secondary" mt={0.5}>
          The radius is set on the restaurant&apos;s Profile, under Food Truck.
        </Typography>
        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {trucks.map((t, ti) => {
            const color = ZONE_COLORS[ti % ZONE_COLORS.length]
            const stillOut = (t.stops || []).some(s => s.status !== 'DONE')
            return (
              <Box key={t.truckId} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={700}>{t.name}</Typography>
                </Box>
                {(t.stops || []).map(({ stop, status }) => (
                  <Box key={stop._id} sx={{ p: 1.25, mb: 0.75, borderRadius: 2, bgcolor: '#f8fafc', opacity: status === 'DONE' ? 0.7 : 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{stop.label || stop.address}</Typography>
                      <Chip label={STATUS[status]?.label || status} size="small" sx={{ ...STATUS[status]?.sx, fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }} />
                    </Box>
                    {stop.label && <Typography variant="caption" color="text.secondary" display="block">{stop.address}</Typography>}
                    <Typography variant="caption" color="text.secondary" display="block">{stop.windowLabel}</Typography>
                    {!hasPoint(stop) && <Typography variant="caption" color="warning.main" display="block">No map location for this stop</Typography>}
                  </Box>
                ))}
                {!t.stops?.length && <Typography variant="caption" color="text.secondary" display="block">No stops today.</Typography>}
                {!stillOut && t.nextLabel && <Typography variant="caption" color="text.secondary" display="block">{t.nextLabel}</Typography>}
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', position: 'relative', minHeight: 320 }}>
        {!pins.length && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 2, px: 1.5, py: 0.75, boxShadow: 2 }}>
            <Typography variant="caption" color="text.secondary">No trucks are out today.</Typography>
          </Box>
        )}
        <Map
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={{ longitude: pins[0]?.stop.longitude ?? -71.43, latitude: pins[0]?.stop.latitude ?? 42.48, zoom: 10 }}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          onLoad={fitToContent}
        >
          {circles.map(p => (
            <Source key={`c-${p.stop._id}`} id={`truck-circle-${p.stop._id}`} type="geojson" data={circleFeature(p.stop, radius)}>
              <Layer id={`truck-circle-fill-${p.stop._id}`} type="fill" paint={{ 'fill-color': p.color, 'fill-opacity': p.status === 'NOW' ? 0.2 : 0.08 }} />
              <Layer id={`truck-circle-line-${p.stop._id}`} type="line"
                paint={{ 'line-color': p.color, 'line-width': 2, ...(p.status === 'NOW' ? {} : { 'line-dasharray': [2, 2] }) }} />
            </Source>
          ))}
          {pins.map(p => (
            <Marker key={`m-${p.stop._id}`} longitude={p.stop.longitude} latitude={p.stop.latitude} anchor="center">
              <div
                title={`${p.truckName} · ${p.stop.label || p.stop.address} · ${STATUS[p.status]?.label || p.status}`}
                style={{ width: 16, height: 16, borderRadius: '50%', background: p.status === 'DONE' ? '#94a3b8' : p.color, border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
              />
            </Marker>
          ))}
        </Map>
      </Box>
    </Box>
  )
}
